import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Injector, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { EditorEventType } from '../../../event-bus/config';
import { EventBus } from '../../../event-bus/lib/event-bus';
import { EventBusHandler } from '../../../event-bus/handler';
import { NavTreeNode } from '../../../interface/tree';
import { TreeComponent } from '../../../components/tree/tree';
import { CommonFileNavTreeDataService } from '../../../nav-data/common-file';
import { ContextService } from '../../../services/context.service';
import { Subscription } from 'rxjs';
import { NavTreeNodeIconService } from '../../../nav-data/node-icon';
import { NavTreeService, RefreshNotification } from '../../../services/nav-tree.service';
import { EditorController } from '../../../controller/editor.controller';
import { CommonNodeIconService } from '../../../nav-data/default-impl/node-icon.service';
import { NavTreeSearchBarConfig } from '../../../interface/nav-panel';
import { NavTreeSearchBarComponent } from './search-bar/search-bar';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TREE_ID_PREFIX } from '../../../components/tree/tree';
import { NavTreeSearchService } from './search-bar/search.service';
import { DesignElementLocaleHandler } from '../../../utils/locale';

/**
 * 通用文件导航树组件
 * @remarks
 * 应该根据相应的接口设置导航树数据以及图标服务
 */
@Component({
  selector: 'common-file-nav-tree',
  templateUrl: './common-file-nav-tree.html'
})
export class CommonFileNavTreeComponent implements AfterViewInit, OnChanges, OnInit, OnDestroy {

  /** 数据为空时的提示信息 */
  @Input() emptyMessage: string = '暂无数据';

  /**
   * 是否主要导航树
   * @remarks
   * 主要导航树会随着当前页签的变化而切换自己的当前选中节点
   * 默认所有导航树都是主要导航树
   */
  @Input() isMainNavTree: boolean = true;

  /** 根路径，作为获取导航树数据时需要传入的参数 */
  get root(): string {
    return this.rootFilePath || this.entryFilePath;
  }

  /**
   * 根文件地址
   * @remarks 默认为空，如果为空则使用上下文服务中的入口文件地址，否则使用该地址作为加载导航树根节点的路径信息
   */
  @Input() rootFilePath: string = null;

  /** 上方搜索栏配置 */
  @Input() searchBarConfig?: NavTreeSearchBarConfig;

  /** 是否禁用搜索服务，提高性能 */
  @Input() disableSearchService?: boolean = false;

  /** 是否启用虚拟列表，默认不启用 */
  @Input() virtualized?: boolean = false;

  /** 树组件实例 */
  @ViewChild("tree") treeCmp: TreeComponent;

  /** 最外层的div元素引用 */
  @ViewChild("wrapper") wrapperEl: ElementRef;

  /** 作为滚动条容器的div元素引用 */
  @ViewChild("scrollContainer") scrollContainerEl: ElementRef;

  /** 作为滚动条滚动内容的外层div元素引用 */
  @ViewChild("scrollElement") scrollElementEl: ElementRef;

  /** 上方的搜索栏组件实例 */
  @ViewChild('searchBar') searchBar: NavTreeSearchBarComponent;

  /**
   * 由文件路径到树节点Id的映射
   * @remarks 在导航树中可能存在多个文件路径相同的节点，所以需要记录右侧编辑器中的文件是由那个节点双击打开的
   */
  private path2IdMap = new Map<string, string>();

  /** 导航树数据 */
  treeData: NavTreeNode[] = [];

  /** 导航树数据服务 */
  private dataService: CommonFileNavTreeDataService = null;
  /** 导航树图标服务 */
  private iconService: NavTreeNodeIconService = null;

  /** 需要在组件销毁时取消的订阅监听 */
  private subscriptions: Subscription[] = [];

  /** 代码视图入口文件路径 */
  private entryFilePath: string = null;

  /** 代码视图事件总线实例 */
  private eventBus: EventBus;

  /** 展平后的节点数组，每次更新数据后由treeData拷贝而来（为搜索功能服务），必须保持同步 */
  private flatNodes: NavTreeNode[] = [];
  /** 搜索关键词，判断本关键词非空时显示搜索结果树 */
  private searchKey: string = '';
  /** 搜索结果树节点数据 */
  private searchResult: NavTreeNode[] = [];

  /** 数据是否为空，为空时隐藏导航树并显示自定义的提示信息 */
  get empty(): boolean {
    if (!!this.searchKey) {
      return !this.searchResult || this.searchResult.length === 0;
    }
    return !this.treeData || this.treeData.length === 0;
  }

  /** 当本字段有值时，优先使用本字段的值控制搜索栏显隐性 */
  private _showSearchBar: boolean = undefined;

  /** 是否显示搜索栏 */
  get showSearchBar(): boolean {
    if (this._showSearchBar === false || this._showSearchBar === true) {
      return this._showSearchBar;
    }
    if (this.searchBarConfig) {
      return this.searchBarConfig.enable;
    }
    return false;
  }

  /** 如果启用虚拟列表则需要为树组件设置高度 */
  get scrollElementStyle(): any {
    return {
      height: this.virtualized ? '100%' : undefined
    };
  }

  /**
   * 待展示的树数据
   * @remarks 搜索关键词非空时展示搜索结果树
   */
  get treeDataToShow(): NavTreeNode[] {
    return this.searchKey ? this.searchResult : this.treeData;
  }

  /**
   * 数据为空时的提示信息
   * @remarks 数据为空或搜索结果为空
   */
  get emptyMessageToShow(): string {
    if (!!this.searchKey) {
      return (this.searchBarConfig && this.searchBarConfig.emptySearchResultTip) || DesignElementLocaleHandler.getValue('codeViewDesigner').searchEmpty ||'搜索结果为空';
    }
    return this.emptyMessage;
  }

  constructor(
    private injector: Injector,  // 防止依赖变更导致需要重新打包
    private cd: ChangeDetectorRef,
    private eventBusHandler: EventBusHandler,
    private context: ContextService,
    private controller: EditorController,
    private navTreeService: NavTreeService,
    private defaultIconService: CommonNodeIconService,
    private searchService: NavTreeSearchService
  ) {
    this.eventBus = this.eventBusHandler.eventBus;
    // 监听导航数据刷新事件
    this.navTreeService.refresh.subscribe((info: RefreshNotification) => {
      this.reloadTreeData(info && info.selectNodePath);
    });
  }

  ngOnInit(): void {
    // 监听入口文件变化
    const subscription = this.context.entryFilePath.subscribe((path: string) => {
      this.entryFilePath = path;
      if (!!this.dataService && !this.rootFilePath) {
        // 如果未指定根文件路径，则采用入口文件作为根文件
        this.reloadTreeData();
      }
    });
    this.subscriptions.push(subscription);
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const isPathChange = !!changes['rootFilePath'];
    if (isPathChange) {
      this.reloadTreeData();
    }
  }

  ngAfterViewInit(): void {
    this.subscribeOuterEvent();
    this.subscribeSearchBar();
    this.cd.detectChanges();
  }

  /** 订阅外部事件 */
  private subscribeOuterEvent(): void {
    // 监听当前标签页切换事件
    this.eventBus.subscribe(EditorEventType.TabSelected, (path: string) => {
      if (!this.isMainNavTree) {
        return;
      }
      this.selectByPath(path);
    }, this);
  }

  /** 处理搜索栏关键词变化事件 */
  private subscribeSearchBar(): void {
    if (!this.searchBar) {
      return;
    }
    const subscription = this.searchBar.textChange.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe((event: any) => {
      const searchKey = event.value;
      // 不能简单的清空选中行，会导致刷新数据时当前行丢失
      // this.treeCmp && this.treeCmp.getTreeTable().clearSelections();  // 防止找不到id警告，直接清空当前的选中条目
      if (searchKey) {
        this.searchResult = this.searchService.search(this.flatNodes, searchKey);
      }
      this.searchKey = searchKey;
      this.cd.detectChanges();
    });
    this.subscriptions.push(subscription);
  }

  /**
   * 直接设置导航树数据和图标服务
   * @remarks 此方法与dataServiceType属性相互冲突，请勿同时调用/设置
   */
  public setDataService(dataService: CommonFileNavTreeDataService, iconService?: NavTreeNodeIconService): void {
    this.dataService = dataService;
    this.iconService = iconService;
    // 注册刷新导航树数据回调函数，让数据服务可以通过该方法控制导航树何时刷新数据
    if (!!this.dataService) {
      this.dataService['refreshNavTreeCallback'] = async () => {
        await this.reloadTreeData();
        this.controller.detectChangesFromRoot();
      };
    }
  }

  /**
   * 隐藏/显示搜索栏
   * @remarks 隐藏前会自动清空输入框
   */
  public toggleSearchBar(): void {
    if (this._showSearchBar === true || this._showSearchBar === false) {
      this._showSearchBar = !this._showSearchBar;
    } else {
      if (this.searchBarConfig && this.searchBarConfig.enable) {
        this._showSearchBar = false;
      } else {
        this._showSearchBar = true;
      }
    }
    if (this._showSearchBar === false) {
      this.clearSearchBar();
    } else {
      setTimeout(() => {
        this.searchBar && this.searchBar.focus();
      });
    }
    this.cd.detectChanges();
  }

  /** 清空搜索栏内容并显示全部数据 */
  private clearSearchBar(): void {
    this.searchBar && this.searchBar.clear();
    this.searchKey = '';  // 由于存在防抖，需要立即切换显示数据
  }

  /**
   * 重新加载导航树节点数据
   * @param selectNodePath 数据加载完成后，选中该路径节点
   */
  public async reloadTreeData(selectNodePath?: string): Promise<void> {
    // 如果刷新数据则自动清空搜索栏，避免不必要的麻烦
    this.clearSearchBar();
    // 如果没有指定节点，则尝试选中之前选中的节点
    selectNodePath = selectNodePath || (
      this.treeCmp.treeTable.selectedRow
      && this.treeCmp.treeTable.selectedRow.data
      && this.treeCmp.treeTable.selectedRow.data.path
    );
    // 如果数据服务或者根路径为空则直接清空树数据
    if (!!this.dataService && !!this.root) {
      try {
        this.treeData = await this.dataService['getChildrenWithValidId'](this.root);
        this.setIcon();
        if ( !this.disableSearchService ) {
          this.flatNodes = this.searchService.cloneDeepAndFlatNodes(this.treeData);
        }
      } catch (err) { console.error('导航树数据加载异常', err); }
      !!this.isMainNavTree && !!selectNodePath && this.selectByPath(selectNodePath);
    } else {
      this.treeData = [];
    }
  }

  /**
   * 通过文件路径获取文件条目标识
   * @remarks 当不存在路径标识映射时，通过该方法获取文件条目标识
   * @param path 文件路径
   * @returns 条目标识
   */
  public getIdByPath(path: string): string {
    const nodes = this.treeData;
    for (const node of nodes) {
      const target = this.findNodeByPath(node, path);
      if (!!target) {
        return target.id;
      }
    }
    return null;
  }
  private findNodeByPath(node: NavTreeNode, path: string): NavTreeNode {
    if (!node || !node.data) {
      return null;
    }
    if (node.data.path === path) {
      return node;
    }
    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        const target = this.findNodeByPath(child, path);
        if (!!target) {
          return target;
        }
      }
    }
    return null;
  }

  /**
   * 清空选中状态
   */
  public clearSelection(): void {
    this.treeCmp.clearSelection();
  }

  /**
   * 根据文件路径选中节点
   * @param path 文件路径
   * @returns 是否成功，如果节点不存在则失败
   */
  public selectByPath(path: string): boolean {
    if (!path) {
      this.clearSelection();
      return;
    }
    // 优先通过路径标识映射获取对应标识
    let id = this.path2IdMap.get(path);
    if (!id) {
      // 映射获取失败则递归遍历树数据
      id = this.getIdByPath(path);
    }
    if (!id || !this.treeCmp.findNode(id)) {
      if (!!id) {
        // 对于刷新导航树后新增加的节点，树组件还没来得及渲染该节点
        // 通过setTimeout来等待树节点渲染完毕，待优化
        setTimeout(() => {
          const ok = !!this.treeCmp.findNode(id);
          if (ok) {
            this.treeCmp.selectById(id);
            // this.scrollNodeIntoView(id);
          }
        }, 150);
      }
      return false;
    }
    this.treeCmp.selectById(id);
    // this.scrollNodeIntoView(id);
    return true;
  }

  /** 尝试将节点自动移入视口 */
  private scrollNodeIntoView(id: string): void {
    this.treeCmp.expandToNodeById(id);
    id = TREE_ID_PREFIX + id;  // 实际渲染在dom中元素的id
    let trEl: HTMLElement = null;
    try {
      trEl = this.wrapperEl.nativeElement.querySelector('#' + id) || document.getElementById(id);
    } catch (err) {  }  // 可能出现id非法报错，不处理
    trEl && trEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /**
   * 处理节点双击事件
   * @remarks 打开文件并选中当前条目
   */
  handleDblClick(node: NavTreeNode): void {
    if (!this.dataService) {
      return;
    }
    const data = node.data;
    // 执行服务自定义节点双击处理回调
    const toOpenFile = this.dataService.handleDblClick(node);
    if (toOpenFile && data.canOpen) {
      // 如果要继续打开文件，则向事件总线发送打开文件通知
      this.eventBus.emit(EditorEventType.OpenFile, { path: data.path, data });
      this.path2IdMap.set(data.path, data.id);
    }
    this.treeCmp.selectById(data.id);
  }

  /** 处理节点展开事件 */
  async handleTreeNodeExpand(node: NavTreeNode): Promise<void> {
    if (!!node.leaf || !this.dataService || !this.dataService.enableLayeredLoading()) {
      return;
    }
    // 如果启用了分层加载机制，且当前节点非叶子节点，则重新加载子节点
    const curPath = node.data.path;
    try {
      const nodes = await this.dataService['getChildrenWithValidId'](this.root, curPath);
      this.setIcon();
      node.children = nodes;
      this.refresh();
      if ( !this.disableSearchService ) {
        this.flatNodes = this.searchService.cloneDeepAndFlatNodes(this.treeData);
      }
    } catch (err) { console.error('导航树数据加载异常', err); }
  }

  onMenuItemClick($event: any): void {
    const { action } = $event;
    switch (action.key) {
      case 'EXPANDALL':
        this.treeCmp.expandAll();
        break;
      case 'COLLAPSEALL':
        this.treeCmp.collapseAll();
        break;
    }
  }

  /** 更新导航树节点图标 */
  private setIcon(): void {
    if (this.treeData) {
      const iconSrv = this.iconService || this.defaultIconService;
      iconSrv.setIconProp(this.treeData);
    }
  }

  /** 触发树组件变更检查 */
  private refresh(): void {
    this.treeData = [...this.treeData];
  }

  /** 获取滚动条所在的元素 */
  public getScrollContainer(): HTMLElement {
    return this.scrollContainerEl && this.scrollContainerEl.nativeElement;
  }
  /** 获取大小变化导致滚动条更新的元素，这里是树列表组件 */
  public getScrollElement(): HTMLElement {
    return this.scrollElementEl && this.scrollElementEl.nativeElement;
  }

}
